import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import Link from 'components/common/Link/Link'
import Eye from 'components/assets/icons/Eye'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import api from 'helpers/api'
import style from 'styles/auth.module.scss'
import EyeClosed from 'components/assets/icons/EyeClosed'
import Loader from 'components/assets/icons/Loader'
import LoginForm from 'components/Auth/LoginLayout/LoginForm'
import { isNewClientLogin } from 'config/loginDesign'

const SignIn = () => {
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { isValid },
    trigger
  } = useForm({
    criteriaMode: 'all',
    delayError: 500,
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onTouched',
  })

  useEffect(() => {
    trigger()
  }, [language, trigger])

  const togglePasswordVisibility = () => {
    setIsPasswordShown(!isPasswordShown)
  }

  const onSubmit = handleSubmit(async data => {
    setLoading(true)

    const signIn = async () => {
      try {
        const res = await api.post({ url: '/auth/clients/signin', data: { email: data.email, password: data.password } })

        if (res.success && res.response.accessToken) {
          localStorage.setItem('accessToken', res.response.accessToken)
          navigate('/contracts')
        } else {
          setErrorMessage('failed')
        }
      } catch (error) {
        setErrorMessage(error.message)
      }
      setLoading(false)
    }

    setTimeout( () => signIn(), 500)
  })

  const messageSelection = () => {
    let message = ''
    switch (errorMessage){
      case 'Wrong password':
        message = t('main:auth.signIn.wrongPwd')
        break;
      case 'Sign up unconfirmed':
        message = t('main:auth.signIn.signUpUnconfirmed')
        break;
      case 'User not registered':
        message = t('main:auth.signIn.unknownUser')
        break;
      case 'Unauthorized':
        message = t('main:auth.signIn.unauthorized')
        break;
      case 'Too many requests':
        message = t('main:auth.signIn.tooManyRequests')
        break;
      default:
        message = t('main:errorMessages.general')
        break;
    }
    return message
  }

  if (isNewClientLogin()) {
    return (
      <LoginForm
        register={register}
        onSubmit={onSubmit}
        isValid={isValid}
        loading={loading}
        errorLabel={errorMessage && messageSelection()}
        forgotTo="/reset-password"
        signUpTo="/sign-up"
      />
    )
  }

  return (
    <>
      <h2 className={style.title}>{t('main:auth.signIn.title')}</h2>
      {errorMessage && <ResponseMessage className={style['response-message']} label={messageSelection()} />}

      <form onSubmit={onSubmit} className={style.form}>
        <div className={style.inputs}>
          <div className={style.input}>
            <Input
              register={register('email', { required: true })}
              darkBg
              label="Email"
              placeholder={`${t('common:your')} ${t('common:form.email.placeholder')}`}
              type="email"
            />
          </div>
          <div className={style.input}>
            <Input
              register={register('password', { required: true })}
              darkBg
              label={t('main:auth.password.password')}
              placeholder={t('main:auth.password.placeholder')}
              type={isPasswordShown ? 'text' : 'password'}
              suffix={
                isPasswordShown ? (
                  <EyeClosed className={classNames(style.eye, 'clickable')} />
                ) : (
                  <Eye className={classNames(style.eye, 'clickable')} />
                )
              }
              onSuffixClick={togglePasswordVisibility}
            />
          </div>
        </div>

        <Button
          type={loading ? 'loading' : 'primary'}
          className={style.button}
          label={loading ? <Loader/> : t('main:auth.signIn.login')}
          onClick={handleSubmit}
          disabled={!isValid || loading || errorMessage === t('main:auth.signIn.tooManyRequests')} />
      </form>

      <Link className={style.link} label={t('main:auth.signIn.forgotPassword')} to={'/reset-password'} darkBg />
      <Link className={style.link} label={t('main:auth.signIn.createAccount')} to={'/sign-up'} darkBg />
    </>
  )
}

export default SignIn
